const MD_BREAKPOINT = 744;

export default function initStateTracker() {
  const stateTrackers = document.querySelectorAll(".state-tracker");
  if (!stateTrackers.length) return;

  stateTrackers.forEach((tracker) => {
    const wrap = tracker.closest(".step-tracker-wrap");
    const trackerLinks = tracker.querySelectorAll("ul li a");
    if (!trackerLinks.length) return;

    const navbarWrapper = document.querySelector(".navbar-wrapper");

    // Placeholder for sticky behavior
    let placeholder = null;

    // Initialize mobile dropdown
    createMobileDropdown(tracker);

    // Update CSS variable for navbar height
    function updateNavbarHeightVar() {
      const height = getNavbarHeight();
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${height}px`
      );
    }

    // Get navbar height
    function getNavbarHeight() {
      if (!navbarWrapper) return 0;
      const navbarContainer = navbarWrapper.querySelector(".navbar-container");
      return navbarContainer ? navbarContainer.offsetHeight : 0;
    }

    // Check if navbar is hidden
    function isNavbarHidden() {
      if (!navbarWrapper) return false;
      return (
        navbarWrapper.classList.contains("scrolled") &&
        navbarWrapper.classList.contains("scroll-down")
      );
    }

    // Get combined offset
    function getTopOffset() {
      let offset = 0;

      // Always add navbar height (like stepTracker)
      offset += getNavbarHeight();
      if (window.innerWidth >= MD_BREAKPOINT) {
        offset += 16;
      } else {
        offset += 8;
      }

      // Add mobile dropdown height if visible
      if (
        window.innerWidth < MD_BREAKPOINT &&
        tracker.classList.contains("is-sticky")
      ) {
        offset += tracker.offsetHeight;
        offset += 8;
      }

      return offset;
    }

    // Update CSS variable for top offset (desktop step-tracker)
    function updateTopOffset() {
      const offset = getTopOffset();
      tracker.style.setProperty("--tracker-top-offset", `${offset}px`);
    }

    // Get target element
    function getTargetElement(href) {
      if (!href || href === "#") return null;
      const id = href.replace("#", "");
      let target = wrap ? wrap.querySelector(`#${id}`) : null;
      if (!target) {
        target = document.getElementById(id);
      }
      return target;
    }

    // Scroll to target
    function scrollToTarget(href) {
      const targetElement = getTargetElement(href);
      if (!targetElement) return;

      const offset = getTopOffset() + 20;
      const targetPosition =
        targetElement.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }

    // Update active state
    function updateActiveState() {
      const topOffset = getTopOffset();
      let activeLink = null;

      trackerLinks.forEach((link) => {
        const href = link.getAttribute("href");
        const targetElement = getTargetElement(href);
        if (!targetElement) return;

        const rect = targetElement.getBoundingClientRect();
        if (rect.top <= topOffset + 50 && rect.bottom > topOffset + 50) {
          activeLink = link;
        }
      });

      // Update desktop links
      trackerLinks.forEach((link) => {
        if (link === activeLink) {
          link.classList.add("active");
        } else {
          link.classList.remove("active");
        }
      });

      // Update mobile dropdown
      const dropdown = tracker.querySelector(".state-tracker-dropdown");
      if (dropdown) {
        const toggleText = dropdown.querySelector(
          ".state-tracker-dropdown-toggle span"
        );
        const dropdownLinks = dropdown.querySelectorAll(
          ".state-tracker-dropdown-menu a"
        );

        dropdownLinks.forEach((link) => link.classList.remove("active"));

        if (activeLink) {
          toggleText.textContent = activeLink.textContent;
          dropdownLinks.forEach((link) => {
            if (link.getAttribute("href") === activeLink.getAttribute("href")) {
              link.classList.add("active");
            }
          });
        }
      }
    }

    // Handle sticky behavior for mobile
    function handleStickyMobile() {
      if (window.innerWidth >= MD_BREAKPOINT) {
        tracker.classList.remove("is-sticky", "is-hidden", "navbar-hidden");
        if (placeholder) {
          placeholder.remove();
          placeholder = null;
        }
        return;
      }

      const spacingSm = 8; // matches --spacing-sm (0.5rem)
      const navbarHeight = getNavbarHeight();
      const navbarHidden = isNavbarHidden();

      // Use placeholder rect if exists (tracker's original position), otherwise use tracker rect
      const checkElement = placeholder || tracker;
      const rect = checkElement.getBoundingClientRect();

      // Trigger point depends on navbar state
      // When navbar hidden: trigger at spacing-xs (element sticks to top)
      // When navbar visible: trigger at navbar-height + spacing-xs (element sticks below navbar)
      const stickyTop = navbarHidden ? spacingSm : navbarHeight + spacingSm;
      const shouldBeSticky = rect.top <= stickyTop;

      // Get the wrap boundaries if exists
      let isWithinWrap = true;
      if (wrap) {
        const wrapRect = wrap.getBoundingClientRect();
        isWithinWrap = wrapRect.bottom > tracker.offsetHeight + stickyTop;
      }

      if (shouldBeSticky && isWithinWrap) {
        if (!tracker.classList.contains("is-sticky")) {
          // Add placeholder to maintain space
          if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.style.height = tracker.offsetHeight + "px";
            tracker.parentNode.insertBefore(placeholder, tracker);
          }
          // IMPORTANT: Add navbar-hidden BEFORE is-sticky to prevent jump when scrolling down
          if (navbarHidden) {
            tracker.classList.add("navbar-hidden");
          }
          tracker.classList.add("is-sticky");
        } else {
          // Update navbar-hidden when already sticky (for scroll up/down transitions)
          if (navbarHidden) {
            tracker.classList.add("navbar-hidden");
          } else {
            tracker.classList.remove("navbar-hidden");
          }
        }
        tracker.classList.remove("is-hidden");
      } else if (!shouldBeSticky) {
        tracker.classList.remove("is-sticky", "is-hidden", "navbar-hidden");
        if (placeholder) {
          placeholder.remove();
          placeholder = null;
        }
      } else if (!isWithinWrap) {
        tracker.classList.add("is-hidden");
      }
    }

    // Handle scroll
    function handleScroll() {
      updateTopOffset();
      handleStickyMobile();
      updateActiveState();
    }

    // Handle desktop link clicks
    trackerLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const href = link.getAttribute("href");
        scrollToTarget(href);
      });
    });

    // Throttled scroll handler
    let ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }

    // Initialize
    updateNavbarHeightVar();
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", () => {
      updateNavbarHeightVar();
      handleScroll();
    });
    handleScroll();
  });

  // Initialize mobile dropdown (use existing HTML structure)
  function createMobileDropdown(tracker) {
    const inner = tracker.querySelector(".state-tracker-inner");
    const dropdown = tracker.querySelector(".state-tracker-dropdown");

    // If HTML structure doesn't exist, skip
    if (!inner || !dropdown) return;

    // Toggle dropdown
    const toggle = dropdown.querySelector(".state-tracker-dropdown-toggle");
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      dropdown.classList.toggle("is-open");
      inner.classList.toggle("dropdown-open");
    });

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove("is-open");
        inner.classList.remove("dropdown-open");
      }
    });

    // Handle dropdown link clicks
    const menu = dropdown.querySelector(".state-tracker-dropdown-menu");
    if (!menu) return;
    const dropdownLinks = menu.querySelectorAll("a");
    dropdownLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        dropdown.classList.remove("is-open");
        inner.classList.remove("dropdown-open");

        const targetId = link.getAttribute("href");
        const targetElement = document.querySelector(targetId);
        if (!targetElement) return;

        const navbarWrapper = document.querySelector(".navbar-wrapper");
        let offset = tracker.offsetHeight + 20;

        if (navbarWrapper) {
          const isNavbarHidden =
            navbarWrapper.classList.contains("scrolled") &&
            navbarWrapper.classList.contains("scroll-down");
          if (!isNavbarHidden) {
            const navbarContainer =
              navbarWrapper.querySelector(".navbar-container");
            if (navbarContainer) {
              offset += navbarContainer.offsetHeight + 8;
            }
          }
        }

        const targetPosition =
          targetElement.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      });
    });
  }
}
